import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarX, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { api, fmtErr } from "../lib/api";

const STATUS_STYLES = {
  confirmed: "bg-emerald-400/10 text-emerald-300 border-emerald-400/30",
  seated: "bg-blue-400/10 text-blue-300 border-blue-400/30",
  completed: "bg-zinc-400/10 text-zinc-300 border-zinc-400/30",
  cancelled: "bg-red-400/10 text-red-300 border-red-400/30",
  no_show: "bg-amber-400/10 text-amber-300 border-amber-400/30",
  pending: "bg-orange-400/10 text-orange-300 border-orange-400/30",
};

export default function MyBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [points, setPoints] = useState(null);

  const load = () =>
    api.get("/bookings/mine")
      .then((r) => { setBookings(r.data); setError(""); })
      .catch((e) => setError(fmtErr(e)))
      .finally(() => setLoading(false));
  useEffect(() => {
    load();
    api.get("/points/mine").then((r) => setPoints(r.data)).catch(() => {});
  }, []);

  const cancellable = (b) =>
    b.status === "confirmed" && new Date(`${b.date}T${b.time}:00`).getTime() > Date.now();

  const cancel = async (id) => {
    try {
      await api.post(`/bookings/${id}/cancel`);
      toast.success("Booking cancelled");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <div data-testid="my-bookings-page" className="max-w-5xl mx-auto px-6 py-16 min-h-screen">
      <p className="text-xs tracking-[0.3em] uppercase text-primary mb-3">Your reservations</p>
      <h1 className="font-display text-4xl tracking-tight">My bookings</h1>

      {points && (
        <div data-testid="loyalty-points-card" className="mt-6 rounded-2xl border border-primary/30 bg-primary/5 p-6 flex items-center justify-between">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-primary">Loyalty points</p>
            <p className="font-outfit font-semibold text-3xl mt-1.5">{points.points} pts</p>
          </div>
          <p className="text-sm text-muted-foreground">{points.visits} completed visit{points.visits === 1 ? "" : "s"} · 10 pts per cover</p>
        </div>
      )}

      <div className="mt-10 space-y-4">
        {bookings.map((b) => (
          <div key={b.id} data-testid={`booking-row-${b.id}`}
            className="rounded-2xl border border-border/60 bg-card p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h3 className="font-display text-xl">{b.restaurant_name}</h3>
                <Badge variant="outline" className={STATUS_STYLES[b.status]}>{b.status.replace("_", " ")}</Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1.5">
                {b.date} at {b.time} · Party of {b.party_size} · Table {b.table_label}
              </p>
            </div>
            {cancellable(b) && (
              <Button data-testid={`cancel-booking-${b.id}`} variant="outline" size="sm" className="rounded-full"
                onClick={() => cancel(b.id)}>
                <CalendarX className="w-4 h-4 mr-2" /> Cancel
              </Button>
            )}
          </div>
        ))}
        {error && <p data-testid="bookings-error" className="text-sm text-destructive text-center py-8">{error}</p>}
        {!loading && bookings.length === 0 && (
          <div className="text-center py-24 text-muted-foreground">
            <MapPin className="w-10 h-10 mx-auto mb-4 opacity-40" />
            <p>No bookings yet.</p>
            <Link to="/restaurants">
              <Button data-testid="discover-restaurants-btn" className="mt-6 rounded-full">Discover restaurants</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
